import { parseCustomizationDefinition } from './customizationDefinition';

/**
 * Helpers for the pdfDocument Customization definition JSON (see PdfDocumentDefinition.cs):
 * the assistant envelope { profile: 'pdfDocument', apiVersion, queries: [{ name, query, ... }],
 * outputs: [{ name: 'document', script }, { name: 'dataStore', script }?], params: [...], notes, ... }
 * with the document script as the output named DOCUMENT_OUTPUT and the optional dataStore script
 * (runs first; returns the [{ scope, key }] rows the document reads as ctx.dataStore["scope:key"])
 * as DATA_STORE_OUTPUT. The server stamps subType / typeVersion at publish; everything else here is
 * author-owned and preserved byte-for-byte in value.
 */

export const PDF_DOCUMENT_PROFILE = 'pdfDocument';
export const DOCUMENT_OUTPUT = 'document';
export const DATA_STORE_OUTPUT = 'dataStore';

/**
 * The pdfscript API version (the P.* surface, PdfScriptContracts) the definition's script is stamped with, or null when it
 * predates the stamp (the server runs it as the pre-stamp default). Distinct from typeVersion (the data contract) and from
 * apiVersion (the OData API the queries are written against).
 */
export const readScriptApiVersion = (definition) => {
  const v = parseCustomizationDefinition(definition)?.scriptApiVersion;
  return typeof v === 'string' && v.trim() ? v.trim() : null;
};

const outputNamed = (def, name) => (Array.isArray(def?.outputs) ? def.outputs : [])
  .find((o) => (o?.name ?? '').toString().trim().toLowerCase() === name.toLowerCase());

/** The document script of a definition, or '' when it has none. */
export const readDocumentScript = (definition) => (outputNamed(parseCustomizationDefinition(definition), DOCUMENT_OUTPUT)?.script ?? '').toString();

/** The dataStore script of a definition (the rows it requests), or '' when it has none. */
export const readDataStoreScript = (definition) => (outputNamed(parseCustomizationDefinition(definition), DATA_STORE_OUTPUT)?.script ?? '').toString();

/** The params[] declarations as pretty JSON text ('' when none declared). */
export const readParamsJson = (definition) => {
  const def = parseCustomizationDefinition(definition);
  return Array.isArray(def?.params) && def.params.length > 0 ? JSON.stringify(def.params, null, 2) : '';
};

/** The queries[] as pretty JSON text ('' when none declared). */
export const readQueriesJson = (definition) => {
  const def = parseCustomizationDefinition(definition);
  return Array.isArray(def?.queries) && def.queries.length > 0 ? JSON.stringify(def.queries, null, 2) : '';
};

/**
 * The assets[] the definition declares, normalised to [{ key, kind }] (see PdfDocumentDefinition.ReadAssets).
 * A template may only reference library assets it declares: the publish gate checks every entry exists.
 */
export const readAssets = (definition) => {
  const def = parseCustomizationDefinition(definition);
  return (Array.isArray(def?.assets) ? def.assets : [])
    .filter((a) => a && typeof a === 'object')
    .map((a) => ({ key: (a.key ?? a.Key ?? '').toString().trim(), kind: (a.kind ?? a.Kind ?? '').toString().trim() }))
    .filter((a) => a.key !== '');
};

/** The script-side reference(s) for a declared asset, by kind — what the Assets tab shows next to each entry. */
export const assetReferenceSnippets = ({ key, kind }) => {
  switch ((kind ?? '').toString().toLowerCase()) {
    case 'text':
      return [`P.richText({ asset: '${key}' })`, `ctx.assets.text['${key}']`];
    case 'pdf':
      return [`P.pdfPage({ asset: '${key}', page: 1, fit: 'width' })`, `P.page({ inserts: [{ asset: '${key}', at: 'end' }] }, header, content, footer)`];
    default:
      return [`P.image('asset:${key}', 'width')`];
  }
};

/**
 * Parses an optional JSON-array editor field. Returns { value } (an array, [] for blank text)
 * or { error } naming the field when the text is not a JSON array.
 */
export const parseJsonArrayField = (text, fieldLabel) => {
  const trimmed = (text ?? '').toString().trim();
  if (!trimmed) return { value: [] };
  try {
    const parsed = JSON.parse(trimmed);
    if (!Array.isArray(parsed)) return { error: `${fieldLabel} must be a JSON array.` };
    return { value: parsed };
  } catch (e) {
    return { error: `${fieldLabel} is not valid JSON: ${e?.message ?? e}` };
  }
};

const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj[key] !== undefined) return obj[key];
  }
  return undefined;
};

/**
 * The params[] declarations in the shape the print dialog's CustomParamsCtrl renders
 * (CustomDocumentParamater: Name, Type, Description, DefaultValue, MultiValue,
 * CustomDocumentParamaterListItems [{ ID, Description }]). The server reads declarations
 * case-insensitively and accepts a few aliases (default, items), so an author's hand-written
 * list is normalised the same way here; entries without a name are dropped.
 */
export const toParamDeclarations = (params) => (Array.isArray(params) ? params : [])
  .filter((p) => p && typeof p === 'object')
  .map((p) => {
    const name = (pick(p, 'Name', 'name') ?? '').toString().trim();
    const items = pick(p, 'CustomDocumentParamaterListItems', 'customDocumentParamaterListItems', 'items');
    const listItems = Array.isArray(items)
      ? items.filter((i) => i && typeof i === 'object').map((i) => ({
        ID: (pick(i, 'ID', 'id', 'Id') ?? '').toString(),
        Description: (pick(i, 'Description', 'description') ?? pick(i, 'ID', 'id', 'Id') ?? '').toString(),
      }))
      : undefined;
    return {
      Name: name,
      Type: (pick(p, 'Type', 'type') ?? (listItems ? 'String' : '')).toString(),
      Description: (pick(p, 'Description', 'description') ?? name).toString(),
      DefaultValue: pick(p, 'DefaultValue', 'defaultValue', 'default') ?? null,
      MultiValue: pick(p, 'MultiValue', 'multiValue') === true,
      CustomDocumentParamaterListItems: listItems,
    };
  })
  .filter((p) => p.Name !== '');

/**
 * Builds the definition to store/preview from the editor's fields, carrying over every
 * other member of the existing definition (notes, sessionOptions, fields, ...) untouched.
 * `typeVersion`, when given, stamps the data-source version the script is written for
 * (the server renders a stamped draft against THAT version — this is how an upgraded
 * script is previewed against the new contract before the record is moved to it).
 * `scriptApiVersion`, when given, stamps the pdfscript API version (P.* surface) the script is
 * written for — the editor passes the server's latest when it takes a script from the assistant
 * (authoring always binds the latest); otherwise the existing stamp is carried over untouched.
 */
export const buildPdfDocumentDefinition = ({ existing, script, dataStoreScript, params, queries, assets, apiVersion, typeVersion, scriptApiVersion, dependentAssets }) => {
  const base = parseCustomizationDefinition(existing) ?? {};
  const nameOf = (o) => (o?.name ?? '').toString().trim().toLowerCase();
  // the dataStore output: replaced when given (blank text removes it), otherwise carried over from the existing definition
  const replaceDataStore = typeof dataStoreScript === 'string';
  const otherOutputs = (Array.isArray(base.outputs) ? base.outputs : [])
    .filter((o) => nameOf(o) !== DOCUMENT_OUTPUT && (!replaceDataStore || nameOf(o) !== DATA_STORE_OUTPUT.toLowerCase()));
  const dataStoreOutputs = replaceDataStore && dataStoreScript.trim() ? [{ name: DATA_STORE_OUTPUT, script: dataStoreScript }] : [];

  const def = {
    ...base,
    profile: PDF_DOCUMENT_PROFILE,
    apiVersion: base.apiVersion ?? apiVersion,
    queries: Array.isArray(queries) ? queries : (Array.isArray(base.queries) ? base.queries : []),
    outputs: [...otherOutputs, ...dataStoreOutputs, { name: DOCUMENT_OUTPUT, script: (script ?? '').toString() }],
    params: Array.isArray(params) ? params : (Array.isArray(base.params) ? base.params : []),
  };
  // assets[] (library declarations, { key, kind }): replaced when given; an empty list drops the member
  if (Array.isArray(assets)) {
    if (assets.length > 0) def.assets = assets.map((a) => ({ key: a.key, kind: a.kind }));
    else delete def.assets;
  }
  // dependentAssets[] ({ key, purpose, designedFromHash }: the library entries the design is made from): same rule
  if (Array.isArray(dependentAssets)) {
    if (dependentAssets.length > 0) def.dependentAssets = dependentAssets;
    else delete def.dependentAssets;
  }
  if (typeof typeVersion === 'string' && typeVersion.trim()) def.typeVersion = typeVersion.trim();
  if (typeof scriptApiVersion === 'string' && scriptApiVersion.trim()) def.scriptApiVersion = scriptApiVersion.trim();
  if (def.apiVersion === undefined) delete def.apiVersion;
  return JSON.stringify(def);
};

/** The dependent assets a definition declares ([{ key, purpose, designedFromHash }]). */
export const readDependentAssets = (definition) => {
  const list = parseCustomizationDefinition(definition)?.dependentAssets;
  return Array.isArray(list) ? list : [];
};

// A new template has NO script: the assistant authors the first version from one of the type's suggested
// prompts (PdfDocument_DataSourceTypes[].SuggestedPrompts), so there is no default to undo or outgrow.
